import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Sparkles, Loader2 } from "lucide-react";

// Post-payment waiting page. Razorpay redirects here immediately after a
// successful payment and appends `razorpay_payment_id` to the URL — we use
// that to poll the backend and find out the moment the account is actually
// ready (created by the Razorpay -> Pabbly -> backend webhook chain, which
// isn't instant and isn't perfectly predictable in timing).
//
// Two conditions both have to be true before we redirect to /login:
//   1. the account is actually ready (polled from the backend)
//   2. at least MIN_DISPLAY_MS has elapsed, even if the account was ready
//      sooner — this is intentional, not a bug, so that tracking pixels on
//      this page get a consistent minimum amount of time to fire.
const MIN_DISPLAY_MS = 6000;
const POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 90000; // after this, stop polling and show a fallback message

const QUOTES = [
  "The mind is not a vessel to be filled, but a chart to be read.",
  "Self-knowledge is the beginning of self-mastery.",
  "What you understand about yourself, you can no longer be ruled by.",
  "Every pattern you notice is a pattern you can change.",
  "Clarity is the first step toward change.",
  "You are not your thoughts — you are the one who notices them.",
];

// MK_PIXEL_PURCHASE_V1: Meta Pixel on the post-payment page
const META_PIXEL_ID = "2298095960760953";

function loadMetaPixel() {
  if (window.fbq) return window.fbq;
  const n = function () {
    if (n.callMethod) n.callMethod.apply(n, arguments);
    else n.queue.push(arguments);
  };
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = "2.0";
  n.queue = [];
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const first = document.getElementsByTagName("script")[0];
  first.parentNode.insertBefore(t, first);
  n("init", META_PIXEL_ID);
  return n;
}

export default function Success() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentId = searchParams.get("razorpay_payment_id");

  const [ready, setReady] = useState(false);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [quoteIndex, setQuoteIndex] = useState(0);
  const [secondsElapsed, setSecondsElapsed] = useState(0);

  const startRef = useRef(Date.now());

  // MK_PIXEL_PURCHASE_V1: PageView always; Purchase once per real Razorpay payment
  useEffect(() => {
    const fbq = loadMetaPixel();
    fbq("track", "PageView");
    if (!paymentId) return;
    const key = "mk_purchase_sent_" + paymentId;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch (e) {
      /* storage blocked: still send once for this page view */
    }
    fbq("track", "Purchase", { currency: "INR", value: 899 }, { eventID: paymentId });
  }, [paymentId]);
  const redirectedRef = useRef(false);

  // Rotate the quote every few seconds, just to hold attention while waiting.
  useEffect(() => {
    const id = setInterval(() => {
      setQuoteIndex((i) => (i + 1) % QUOTES.length);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  // Tick a simple elapsed-seconds counter for the on-screen counter/animation.
  useEffect(() => {
    const id = setInterval(() => {
      setSecondsElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, []);

  // Enforce the minimum display time independently of polling.
  useEffect(() => {
    const id = setTimeout(() => setMinTimeElapsed(true), MIN_DISPLAY_MS);
    return () => clearTimeout(id);
  }, []);

  // Poll the backend for account readiness.
  useEffect(() => {
    if (!paymentId) {
      // No payment_id in the URL at all — nothing to poll. Don't hang forever;
      // fall back to "just wait the minimum time then send them to login",
      // since the email will get there regardless of this page.
      setReady(true);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - startRef.current > MAX_WAIT_MS) {
        setTimedOut(true);
        return;
      }
      try {
        const res = await fetch(`/api/payment-status/${encodeURIComponent(paymentId)}`);
        const data = await res.json();
        if (cancelled) return;
        if (data.ready) {
          setReady(true);
          return;
        }
      } catch {
        // Network hiccup — just try again on the next tick, no need to alarm the user.
      }
      if (!cancelled) setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  // Once both conditions are true, redirect exactly once.
  useEffect(() => {
    if (ready && minTimeElapsed && !redirectedRef.current) {
      redirectedRef.current = true;
      navigate("/login");
    }
  }, [ready, minTimeElapsed, navigate]);

  const remainingSeconds = Math.max(0, Math.ceil((MIN_DISPLAY_MS - (secondsElapsed * 1000)) / 1000));

  return (
    <div
      data-testid="success-root"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: "radial-gradient(circle at 50% 30%, #14102A 0%, #03030A 70%)",
        color: "#F4F6F0",
      }}
    >
      <style>{`
        @keyframes mk-success-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .mk-success-spin {
          animation: mk-success-spin 1.4s linear infinite;
        }
      `}</style>
      <div className="mandala-watermark" />
      <div className="grain-overlay" />

      <div
        className="glass-panel"
        style={{
          maxWidth: 560,
          width: "100%",
          padding: "3rem 2rem",
          borderRadius: 22,
          textAlign: "center",
          position: "relative",
          zIndex: 3,
        }}
      >
        {timedOut ? (
          <>
            <CheckCircle2 size={44} style={{ color: "#50C878" }} />
            <h1 className="serif" style={{ fontSize: "2.2rem", margin: "1.2rem 0 0.6rem", fontWeight: 500 }}>
              Almost <span style={{ color: "#D4AF37", fontStyle: "italic" }}>ready.</span>
            </h1>
            <p style={{ color: "#A0A0B0", lineHeight: 1.65, margin: "0 0 2rem" }}>
              Your payment went through and your account is being set up now.
              This is taking a little longer than usual — check your email in
              a few minutes for your login details, or try logging in directly.
            </p>
            <Link to="/login" className="ghost-button" data-testid="success-go-to-login">
              <Sparkles size={14} /> Go to login
            </Link>
          </>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "1.2rem" }}>
              <Loader2 size={44} style={{ color: "#D4AF37" }} className="mk-success-spin" />
            </div>
            <h1 className="serif" style={{ fontSize: "2.2rem", margin: "0 0 0.6rem", fontWeight: 500 }}>
              Setting up your <span style={{ color: "#D4AF37", fontStyle: "italic" }}>account.</span>
            </h1>
            <p
              style={{
                color: "#A0A0B0",
                lineHeight: 1.65,
                margin: "0 0 1.8rem",
                fontStyle: "italic",
                minHeight: "3.2em",
              }}
              key={quoteIndex}
            >
              "{QUOTES[quoteIndex]}"
            </p>
            <div
              style={{
                fontSize: "0.85rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#6E6E82",
              }}
            >
              {remainingSeconds > 0 ? `Ready in ${remainingSeconds}...` : "Almost there..."}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
